import React, { useState, useRef, useEffect, useId } from 'react';
import {
  Container,
  Progress,
  Card,
  CardBody,
  Row,
  Col,
  Collapse,
} from 'reactstrap';
import { FaLock, FaLockOpen } from 'react-icons/fa';
import { useUserAuth } from "../../context/UserContext";
import { isPrivilegedRole } from "../../utils/roles";
import { getFreshIdToken } from "../../utils/authToken";
import { requestLessonsData } from "../../services/userService";
import DemoNavbar from "../../components/Navbars/DemoNavbar.js";
import { CLAUDE_DEV_COURSE_LESSONS } from "./devLessonsRegistry";
import '../examples/TableOfContent.css';
import { useTranslation } from "react-i18next";
import { TocLesson, LessonStatus, LockedNote, useLockedNote } from "../../components/CourseToc/CourseToc";

// Badge colours for the module circles — one per module (5 total).
const MODULE_COLORS = [
  { bg: '#DBEAFE', text: '#2563EB' },
  { bg: '#EDE9FE', text: '#7C3AED' },
  { bg: '#DCFCE7', text: '#16A34A' },
  { bg: '#FEF3C7', text: '#D97706' },
  { bg: '#FEE2E2', text: '#DC2626' },
];

// Group the flat registry into an array of module arrays, ordered by module
// number parsed from each lesson's route (e.g. "CD_M3_L2" -> module 3).
// Registry routes carry no leading slash, so onClick prepends one.
function buildModules() {
  const groups = {};
  CLAUDE_DEV_COURSE_LESSONS.forEach((l) => {
    const match = l.route.match(/CD_M(\d+)_/);
    const moduleNum = match ? parseInt(match[1], 10) : 1;
    if (!groups[moduleNum]) groups[moduleNum] = [];
    groups[moduleNum].push({
      title: l.title,
      lessonID: l.lessonID,
      completed: false,
      locked: true,
      onClick: `/${l.route}`,
    });
  });
  return Object.keys(groups)
    .sort((a, b) => Number(a) - Number(b))
    .map((k) => groups[k]);
}

// Within a module, unlock completed lessons plus the first incomplete one; lock the rest.
function applySequentialUnlocksWithinModule(lessons) {
  let firstIncompleteUnlocked = false;
  return lessons.map((l) => {
    if (l.completed) {
      return { ...l, locked: false };
    }
    if (!firstIncompleteUnlocked) {
      firstIncompleteUnlocked = true;
      return { ...l, locked: false };
    }
    return { ...l, locked: true };
  });
}

// A module stays fully locked until the previous module is fully complete.
function applyCrossModuleUnlocks(modules) {
  const updated = modules.map((m) => [...m]);

  for (let i = 1; i < updated.length; i++) {
    const prevModule = updated[i - 1];
    const prevDone = prevModule.every((l) => l.completed);

    if (!prevDone) {
      updated[i] = updated[i].map((l) => ({ ...l, locked: true }));
    } else {
      updated[i] = updated[i].map((l, idx) => (idx === 0 ? { ...l, locked: false } : l));
    }
  }
  return updated;
}

// Apply within-module then cross-module gating to a set of module arrays.
function applyLocks(modules) {
  return applyCrossModuleUnlocks(modules.map(applySequentialUnlocksWithinModule));
}

const ClaudeDevTableComponent = () => {
  const mainRef = useRef(null);
  const { user } = useUserAuth();
  const { t } = useTranslation();

  // Privileged roles (admin/tester) see every lesson unlocked regardless of
  // completion, so they can navigate the whole course freely for QA.
  const privileged = isPrivilegedRole(user?.role);

  // Modules built from the registry; lock state seeded so Module 1 / Lesson 1 is open on first paint.
  const [modules, setModules] = useState(() => applyLocks(buildModules()));

  const allLessons = modules.flat();
  const completedCount = allLessons.filter((l) => l.completed).length;
  const totalCount = allLessons.length;
  const progress = totalCount ? Math.round((completedCount / totalCount) * 100) : 0;

  // Keyed on the user id so the fetch re-runs when auth resolves: on a client-side
  // navigation the component can paint before `user` is populated, and the old
  // one-shot effect bailed out silently and never retried — which is why progress
  // showed as 0% until the page was reloaded.
  useEffect(() => {
    let cancelled = false;

    const loadProgress = async () => {
      if (!user) return;

      // Mint a fresh token rather than reusing the (possibly hours-old) snapshot on
      // `user`: an expired token fails verification server-side and requestLessonsData
      // swallows the error into [], which reads as "nothing completed".
      const token = await getFreshIdToken(user.accessToken);
      if (!token || cancelled) return;

      const result = await requestLessonsData(token, "CD01");
      if (cancelled) return;

      const completedIDs = new Set(
        (result || []).filter((r) => r.lessonCompleted === true).map((r) => r.lessonID)
      );

      // Rebuild from the registry, mark completion from the fetch, then re-apply gating.
      const merged = buildModules().map((mod) =>
        mod.map((l) => ({ ...l, completed: completedIDs.has(l.lessonID) }))
      );
      setModules(applyLocks(merged));
    };

    loadProgress();
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  return (
    <>
      <DemoNavbar />
      <main ref={mainRef}>
        <section className="section section-lg bg-primary-gray">
          <Container className="py-5">
            <div className="mb-4">
              <Row className="align-items-center justify-content-between mb-2">
                <Col><h1 className="h2 fw-bold text-dark">{t("table.claudeDevTableT")}</h1></Col>
                <Col className="text-end">
                  <small className="text-muted" id="toc-progress-label">{t("table.yourProgress")}</small> <span className="fw-medium">{progress}%</span>
                </Col>
              </Row>
              <Progress
                value={progress}
                color="primary"
                style={{ height: '10px' }}
                barAriaLabelledBy="toc-progress-label"
                barAriaValueText={t("table.progressSummary", { done: completedCount, total: totalCount })}
              />
            </div>

            <Card className="mb-4">
              <CardBody>
                <Row className="align-items-center justify-content-between">
                  <Col md="6">
                    <h2 className="h5 fw-semibold mb-1">{t("table.tableOfContents")}</h2>
                    <p className="text-muted mb-0">{t("table.completeLessonToUnlock")}</p>
                  </Col>
                  <Col md="6" className="d-flex justify-content-end gap-4">
                    <div className="text-center">
                      <small className="text-muted d-block">{t("table.totalLessons")}</small>
                      <span className="fw-medium">{totalCount}</span>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {modules.map((lessons, idx) => (
              <Module
                key={idx}
                title={`table.module${idx + 1}`}
                number={idx + 1}
                color={MODULE_COLORS[idx % MODULE_COLORS.length]}
                lessons={lessons}
                privileged={privileged}
              />
            ))}

            <Card className="mt-4 border-primary bg-light">
              <CardBody className="d-flex">
                <div>
                  <h2 className="h6 fw-semibold text-primary">{t("table.keepGoing")}</h2>
                  <p className="text-primary small mb-0">
                    {t("table.youCompleted")} {completedCount} {t("table.of")} {totalCount} {t("table.lessons")} ({progress}%){t("table.completedAll")}
                  </p>
                </div>
              </CardBody>
            </Card>
          </Container>
        </section>
      </main>
    </>
  );
};

const Module = ({ title, number, color, lessons, privileged }) => {
  const [open, setOpen] = useState(number === 1);
  const [lockedNote, showLockedNote] = useLockedNote();
  const listId = useId();
  const { t } = useTranslation();

  return (
    <Card className="mb-3 shadow-sm custom-card">
      <h3 className="toc-module-heading">
        <button
          type="button"
          className="toc-module-toggle d-flex justify-content-between align-items-center p-3 "
          aria-expanded={open}
          aria-controls={listId}
          onClick={() => setOpen(!open)}
        >
          <span className="d-flex align-items-center">
            <span
              className="rounded-circle d-flex justify-content-center align-items-center me-3"
              style={{
                width: '32px',
                height: '32px',
                backgroundColor: color.bg,
                color: color.text,
                fontWeight: 'bold',
              }}
            >
              {number}
            </span>
            <span className="h5 mb-0 text-dark fw-semibold">{t(title)}</span>
          </span>
        </button>
      </h3>
      <Collapse isOpen={open} id={listId}>
        <div className="border-top ">
          {lessons.map((lesson, idx) => {
            // Privileged users see everything unlocked regardless of completion.
            const locked = privileged ? false : lesson.locked;
            return (
            <TocLesson
              key={`${lesson.lessonID ?? lesson.title}-${idx}`}
              to={lesson.onClick}
              locked={locked}
              onLocked={showLockedNote}
              className={`lesson-item p-3 m-3 d-flex justify-content-between align-items-center ${lesson.completed ? 'left-border-green' : 'left-border-gray'} ${locked ? 'opacity-75' : ''}`}
              style={{ cursor: locked ? 'not-allowed' : 'pointer' }}
            >
              <span className="d-flex  align-items-center ">
                <span className="position-relative me-2">
                  <span className="  d-flex align-items-center justify-content-center" />
                </span>
                <span className="fw-medium text-dark">{t(`cdTitles.${lesson.lessonID}`, { defaultValue: lesson.title })}</span>
              </span>
              <span className="d-flex align-items-center gap-2">
                {locked ? (
                  <FaLock className="text-muted" aria-hidden="true" />
                ) : (
                  <FaLockOpen className="text-success" aria-hidden="true" />
                )}
                <LessonStatus completed={lesson.completed} locked={locked} />
              </span>
            </TocLesson>
            );
          })}
          <LockedNote count={lockedNote} />
        </div>
      </Collapse>
    </Card>
  );
};

export default ClaudeDevTableComponent;
